import React, { PureComponent } from 'react'
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Spin,
  notification,
} from 'antd'
import moment from 'moment'
import Request from '../../../request'
import MediaViewer from '../../../components/MediaViewer'
import styles from './styles.less'

const parseMedia = (data) => {
  if (!data) return { photos: [], videos: [] }
  if (Array.isArray(data)) {
    const photos = []
    const videos = []
    data.forEach((m) => {
      const type = (m && m.type) || ''
      if (type === 'video') videos.push(m)
      else if (type === 'image' || type === 'photo') photos.push(m)
      else photos.push(m)
    })
    return { photos, videos }
  }
  return {
    photos: data.photos || data.images || [],
    videos: data.videos || [],
  }
}

class InwardReviewModal extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      mediaLoading: false,
      photos: [],
      videos: [],
      inwardMap: {}, // inwardId -> dispatch (inward form) record
    }
    this.dirty = false
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible && this.props.group) {
      this.dirty = false
      this.fetchMedia()
    }
  }

  fetchMedia = () => {
    const { group } = this.props
    if (!group || !group.key) return
    const startDate = moment(group.key, 'YYYY-MM-DD').startOf('day').toISOString()
    const endDate = moment(group.key, 'YYYY-MM-DD').endOf('day').toISOString()
    this.setState({ mediaLoading: true, photos: [], videos: [] })
    Request.inwardMediaByDate({
      captureDate: group.key,
      startDate,
      endDate,
      orderIds: (group.orderIds || []).join(','),
      _t: Date.now(),
    }).then(({ error, data }) => {
      if (!error) {
        const { photos, videos } = parseMedia(data)
        this.setState({ mediaLoading: false, photos, videos })
        this.fetchInwardForms([...photos, ...videos])
      } else {
        this.setState({ mediaLoading: false })
        notification.error({ message: 'Failed to load media' })
      }
    })
  }

  // fetch the inward (dispatch) form record for each photo's inwardId so the
  // attached form details can be shown in the image preview
  fetchInwardForms = (items) => {
    const ids = []
    ;(items || []).forEach((p) => {
      if (p && p.inwardId && ids.indexOf(String(p.inwardId)) === -1) {
        ids.push(String(p.inwardId))
      }
    })
    ids.forEach((id) => {
      if (this.state.inwardMap[id]) return
      Request.getDispatchInwardOutwardById({ _id: id }).then(({ data, error }) => {
        if (!error && data) {
          this.setState((s) => ({ inwardMap: { ...s.inwardMap, [id]: data } }))
        }
      })
    })
  }

  // details table of the inward form attached to the currently previewed image
  renderInwardDetails = (current) => {
    const m = (current && current.raw) || {}
    const id = m.inwardId ? String(m.inwardId) : null
    const d = id ? this.state.inwardMap[id] : null
    if (!d) return null
    const products = d.products || []
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
          Inward Form Details
        </div>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Serial No.">{d.serialNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Type">{d.type || '-'}</Descriptions.Item>
          <Descriptions.Item label="Department">{d.department || '-'}</Descriptions.Item>
          <Descriptions.Item label="Purpose">{d.purpose || '-'}</Descriptions.Item>
          <Descriptions.Item label="Challan Number">{d.challanNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Consignee">{d.consignee || '-'}</Descriptions.Item>
          <Descriptions.Item label="Carrier">{d.carrier || '-'}</Descriptions.Item>
          <Descriptions.Item label="Date">
            {d.date ? moment(d.date).format('DD-MM-YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Products">
            {products.length
              ? products.map((p, i) => {
                  const qty =
                    p.quantityRecieved != null
                      ? p.quantityRecieved
                      : p.quantity != null
                      ? p.quantity
                      : p.quantityAccepted
                  return (
                    <div key={i} style={{ marginBottom: 2 }}>
                      {p.productName || '-'}
                      {qty != null && qty !== '' ? (
                        <span style={{ color: '#888' }}> · Qty: {qty}</span>
                      ) : null}{' '}
                      <Tag
                        color={p.subjectedToStock ? 'green' : ''}
                        style={{ marginLeft: 4 }}
                      >
                        Stock: {p.subjectedToStock ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                  )
                })
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </div>
    )
  }

  handleItemReview = async (item, status, remarks) => {
    if (!item || !item._id) {
      notification.error({ message: 'Missing media id' })
      throw new Error('missing media id')
    }
    let res
    if (status === 'Rejected') {
      res = await Request.rejectInwardMedia({ mediaId: item._id, remarks: remarks || '' })
    } else {
      res = await Request.completeInwardMedia({ mediaId: item._id })
    }
    if (res.error) {
      notification.error({ message: res.message || 'Failed to update media' })
      throw new Error('review failed')
    }
    notification.success({
      message: res.message || `Media marked as ${status === 'Completed' ? 'Approved' : status}`,
    })
    this.dirty = true
    // also patch this modal's own copy of the item — MediaViewer rebuilds its
    // list from these arrays whenever they change (e.g. after deleting another
    // item), so a stale status here would visually revert this review
    const patch = (list) =>
      list.map((m) =>
        String(m._id) === String(item._id)
          ? { ...m, status, reviewRemarks: status === 'Rejected' ? remarks || '' : '' }
          : m
      )
    this.setState((s) => ({ photos: patch(s.photos), videos: patch(s.videos) }))
    return status
  }

  handleClose = () => {
    const { onUpdated, onClose } = this.props
    if (this.dirty && onUpdated) onUpdated()
    this.dirty = false
    if (onClose) onClose()
  }

  // Open the dedicated "Deleted Inward Images" page for this capture-date group
  // in a new tab (a full page handles many images and the restore confirm far
  // better than a nested modal).
  openDeletedPage = () => {
    const { group } = this.props
    const qs = []
    if (group && group.key) qs.push(`captureDate=${encodeURIComponent(group.key)}`)
    if (group && group.orderIds && group.orderIds.length) {
      qs.push(`orderIds=${encodeURIComponent(group.orderIds.join(','))}`)
    }
    window.open(`/inwardOrders/deleted${qs.length ? `?${qs.join('&')}` : ''}`, '_blank')
  }

  deleteMedia = async (item) => {
    if (!item || !item._id) return
    const res = await Request.deleteInwardMedia({ mediaId: item._id })
    if (res && !res.error) {
      notification.success({ message: 'Media deleted' })
      this.dirty = true
      this.setState((s) => ({
        photos: s.photos.filter((p) => String(p._id) !== String(item._id)),
        videos: s.videos.filter((p) => String(p._id) !== String(item._id)),
      }))
    } else {
      notification.error({ message: (res && res.message) || 'Failed to delete media' })
    }
  }

  renderItemExtra = (item) => {
    const m = (item && item.raw) || {}
    if (!(m.orderId || m.userName || m.subCategory)) return null
    return (
      <div className={styles.itemMeta}>
        {m.orderId ? <span className={styles.itemMetaPart}>Order #{m.orderId}</span> : null}
        {m.userName ? <span className={styles.itemMetaPart}>{m.userName}</span> : null}
        {m.subCategory ? <span className={styles.itemMetaPart}>{m.subCategory}</span> : null}
      </div>
    )
  }

  render() {
    const { visible, group } = this.props
    const { mediaLoading, photos, videos } = this.state

    if (!group) return null

    const photosCount = photos.length
    const videosCount = videos.length

    return (
      <Modal
        visible={visible}
        onCancel={this.handleClose}
        footer={null}
        width={'100%'}
        style={{ top: 0, maxWidth: '100%', margin: 0, paddingBottom: 0 }}
        bodyStyle={{ height: 'calc(100vh - 55px)', overflowY: 'auto', padding: '16px 24px' }}
        wrapClassName={styles.fullModalWrap}
        title={
          <span>
            Inward Media — {group.label}{' '}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {group.mediaCount || photosCount + videosCount} Media
            </Tag>
          </span>
        }
        destroyOnClose
      >
        <Descriptions
          size={'small'}
          column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          bordered
        >
          <Descriptions.Item label={<b>Capture Date</b>}>{group.label}</Descriptions.Item>
          <Descriptions.Item label={<b>Orders</b>}>{group.orderCount || '-'}</Descriptions.Item>
          <Descriptions.Item label={<b>Photos</b>}>{photosCount}</Descriptions.Item>
          <Descriptions.Item label={<b>Videos</b>}>{videosCount}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 12, color: '#888' }}>
          Showing all inward photos &amp; videos captured on <b>{group.label}</b>.
          Mark each item <b>Approved</b> or <b>Reject</b> (with a reason).
        </div>

        <div style={{ marginTop: 12 }}>
          {mediaLoading ? (
            <div className={styles.mediaLoading}>
              <Spin tip="Loading media..." />
            </div>
          ) : (
            <MediaViewer
              reviewable
              rejectWithReason
              hideControlsWhenReviewed
              completeLabel="Approve"
              statusLabels={{ Completed: 'Approved' }}
              photos={photos}
              videos={videos}
              onReview={this.handleItemReview}
              onDelete={this.deleteMedia}
              renderItemExtra={this.renderItemExtra}
              detailsPanel={this.renderInwardDetails}
            />
          )}
        </div>

        <div className={styles.modalFooter} style={{ textAlign: 'center' }}>
          <Button icon="delete" onClick={this.openDeletedPage} style={{ marginRight: 8 }}>
            Deleted Images
          </Button>
          <Button onClick={this.handleClose}>Close</Button>
        </div>
      </Modal>
    )
  }
}

export default InwardReviewModal
